import React, { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, Eye, X } from "@phosphor-icons/react";
import api from "@/lib/api";

export const EMPTY_NOTIFY = { on: false, note: "" };

// Axios config that asks the server to email the buyer about this save.
export function notifyParams(n) {
  if (!n?.on) return undefined;
  const note = n.note.trim();
  return { params: { notify_buyer: true, ...(note ? { buyer_note: note } : {}) } };
}

const STATUS_STYLE = { ready: "bg-emerald-50 border-emerald-700", skipped: "bg-amber-50 border-amber-600", nothing: "bg-zinc-100 border-black" };

function EmailPreviewModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="buyer-email-preview-modal">
      <div className="my-8 w-full max-w-2xl border-2 border-black bg-white shadow-[6px_6px_0_0_#000]">
        <div className="flex items-center gap-3 border-b-2 border-black px-4 py-3">
          <div>
            <div className="label-mono text-zinc-500">Email preview</div>
            <div className="font-chivo text-lg font-black leading-tight">What the buyer will get</div>
          </div>
          <button type="button" className="ml-auto p-1 hover:bg-zinc-100" onClick={onClose} aria-label="Close" data-testid="buyer-email-preview-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="space-y-3 p-4 text-xs">
          <div className={`border-2 px-3 py-2 ${STATUS_STYLE[data.status] || STATUS_STYLE.nothing}`} data-testid="buyer-email-preview-status">{data.message}</div>
          {(data.emails || []).map((m, i) => {
            const sfx = i ? `-${i}` : "";
            return (
              <div key={i} className="space-y-2 border-t-2 border-dashed border-zinc-300 pt-3 first:border-0 first:pt-0">
                {data.emails.length > 1 && (
                  <div className={`border-2 px-2 py-1 ${STATUS_STYLE[m.status]}`} data-testid={`buyer-email-preview-item-status${sfx}`}>
                    Email {i + 1}{m.firm_name ? ` · ${m.firm_name}` : ""}: {m.message}
                  </div>
                )}
                <div data-testid={`buyer-email-preview-to${sfx}`}><span className="label-mono">To</span> {m.to || "No contact email"}</div>
                <div data-testid={`buyer-email-preview-subject${sfx}`}><span className="label-mono">Subject</span> {m.subject}</div>
                <iframe title={`Buyer email preview ${i + 1}`} srcDoc={m.html} sandbox="" className="h-[480px] w-full border-2 border-black bg-white"
                        data-testid={`buyer-email-preview-frame${sfx}`} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// "Email the buyer about this change" tick box + optional note + preview, shown next to Save.
export function NotifyBuyerBox({ value, onChange, testId = "notify-buyer", preview }) {
  const [shown, setShown] = useState(null);
  const [busy, setBusy] = useState(false);
  const openPreview = async () => {
    const body = preview?.();
    if (!body) return toast.error("Fill in the change first");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/buyer-email-preview", { ...body, note: value.note.trim() || null });
      setShown(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't build the preview");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="border-2 border-black bg-zinc-50 px-2.5 py-2 text-[11px]" data-testid={testId}>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="checkbox" className="accent-black" checked={value.on}
               onChange={(e) => onChange({ ...value, on: e.target.checked })} data-testid={`${testId}-check`} />
        <EnvelopeSimple size={12} weight="bold" /> Email the buyer about this change
      </label>
      {value.on && (
        <>
          <textarea rows={2} maxLength={1000} value={value.note} placeholder="Add a note for the buyer (optional)"
                    onChange={(e) => onChange({ ...value, note: e.target.value })}
                    className="mt-1.5 w-full border-2 border-black bg-white px-2 py-1 text-xs" data-testid={`${testId}-note`} />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {preview && (
              <button type="button" onClick={openPreview} disabled={busy} data-testid={`${testId}-preview`}
                      className="inline-flex items-center gap-1 border-2 border-black bg-white px-2 py-1 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40 transition-colors duration-100">
                <Eye size={11} weight="bold" /> {busy ? "Building…" : "Preview email"}
              </button>
            )}
            <span className="text-[10px] text-zinc-600">
              Goes to their main contact email and lists what changed in this save. Nothing is sent if nothing changed.
            </span>
          </div>
        </>
      )}
      {shown && <EmailPreviewModal data={shown} onClose={() => setShown(null)} />}
    </div>
  );
}
